import { z } from "zod";
import { reviewStateSchema } from "./review";

export const exclusionFields = {
  occurred_at: "Signal occurred",
  observed_at: "Signal observed",
  person_name: "Person name",
  person_status: "Person status",
  username: "Username",
  bio: "Bio",
  email: "Public email",
  email_domain: "Email domain",
  website_host: "Person website host",
  company_name: "Company name",
  company_domain: "Company domain",
  company_website_host: "Company website host",
} as const;
export const exclusionOperators = {
  older: "Older than",
  equals: "Equals",
  contains: "Contains",
  domain: "Domain and subdomains",
} as const;
export const exclusionKindLabels = {
  person: "Person",
  company: "Company",
  identity: "Account",
  activity: "Signal",
} as const;
export const exclusionKinds = ["person", "company", "identity", "activity"] as const;
export const exclusionKindSchema = z.enum(exclusionKinds);
export function isSignalDateField(field: string | null | undefined): boolean {
  return field === "occurred_at" || field === "observed_at";
}
export const signalAgeSchema = z
  .string()
  .regex(/^[1-9][0-9]* (days|weeks|months|years)$/)
  .refine((value) => {
    const [amount, unit] = value.split(" ");
    // PostgreSQL intervals store months and days as signed 32-bit integers.
    const multiplier = unit === "years" ? 12 : unit === "weeks" ? 7 : 1;
    return Number(amount) * multiplier <= 2147483647;
  });
export const exclusionInputSchema = z
  .object({
    name: z.string().trim().min(1),
    enabled: z.boolean().default(true),
    field: z
      .enum(
        Object.keys(exclusionFields) as [
          keyof typeof exclusionFields,
          ...Array<keyof typeof exclusionFields>,
        ],
      )
      .nullable()
      .default(null),
    operator: z.enum(["equals", "contains", "domain", "older"]).nullable().default(null),
    value: z.string().trim().min(1).nullable().default(null),
    provider: z.string().trim().min(1).nullable().default(null),
    personId: z.uuid().nullable().default(null),
    companyId: z.uuid().nullable().default(null),
    identityId: z.uuid().nullable().default(null),
  })
  .strict()
  .superRefine((v, ctx) => {
    const exact = [v.personId, v.companyId, v.identityId].filter(Boolean).length;
    if (
      exact
        ? exact !== 1 || v.field || v.operator || v.value || v.provider
        : !v.field || !v.operator || !v.value
    )
      ctx.addIssue({
        code: "custom",
        message: "Choose one record or one field condition.",
      });
    if (
      v.field === "person_status" &&
      (v.operator !== "equals" || !reviewStateSchema.safeParse(v.value).success)
    )
      ctx.addIssue({
        code: "custom",
        message: "Person status requires Equals and a valid status.",
      });
    const date = isSignalDateField(v.field);
    if (
      date
        ? v.operator !== "older" || !signalAgeSchema.safeParse(v.value).success
        : v.operator === "older"
    )
      ctx.addIssue({
        code: "custom",
        message: "Signal dates require Older than and a positive duration, such as 3 months.",
      });
    const domain =
      v.field &&
      ["email_domain", "website_host", "company_domain", "company_website_host"].includes(v.field);
    if ((v.operator === "domain" && !domain) || (domain && v.operator === "contains"))
      ctx.addIssue({
        code: "custom",
        message: "Domains support exact or domain-and-subdomain matching.",
      });
    if (v.provider && v.field !== "username")
      ctx.addIssue({
        code: "custom",
        message: "Provider applies only to username rules.",
      });
  });
export type ExclusionInput = z.infer<typeof exclusionInputSchema>;
export const exclusionRuleSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  enabled: z.boolean(),
  field: z.string().nullable(),
  operator: z.string().nullable(),
  value: z.string().nullable(),
  provider: z.string().nullable(),
  personId: z.uuid().nullable(),
  companyId: z.uuid().nullable(),
  identityId: z.uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdByName: z.string(),
});
export type ExclusionRule = z.infer<typeof exclusionRuleSchema>;
export const exclusionReasonSchema = z.object({
  ruleId: z.uuid(),
  name: z.string(),
  field: z.string(),
  value: z.string(),
  viaKind: exclusionKindSchema.nullable(),
  viaId: z.uuid().nullable(),
  viaName: z.string().nullable(),
});
export type ExclusionReason = z.infer<typeof exclusionReasonSchema>;
export const excludedRecordSchema = z.object({
  id: z.uuid(),
  kind: exclusionKindSchema,
  name: z.string(),
  context: z.string().nullable(),
  reasons: z.array(exclusionReasonSchema),
});
export const exclusionListQuerySchema = z.object({
  q: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1),
  kind: exclusionKindSchema.optional(),
  ruleId: z.uuid().optional(),
  sort: z.enum(["name", "kind", "updated"]).default("name"),
  direction: z.enum(["asc", "desc"]).default("asc"),
});
export const excludedRecordsQuerySchema = exclusionListQuerySchema.extend({ ruleId: z.uuid() });
export const exclusionRulesSchema = z.object({
  items: z.array(exclusionRuleSchema),
  total: z.number(),
  page: z.number(),
});
export const excludedRecordsSchema = z.object({
  items: z.array(excludedRecordSchema),
  total: z.number(),
  page: z.number(),
});
export const exclusionPreviewSchema = z.object({
  total: z.number(),
  newlyExcluded: z.number(),
  people: z.number(),
  companies: z.number(),
  activities: z.number(),
  identities: z.number(),
  items: z.array(excludedRecordSchema),
});
export const exclusionContextSchema = z.object({
  reasons: z.array(exclusionReasonSchema),
  targets: z.array(
    z.object({
      kind: z.enum(["person", "company", "identity"]),
      id: z.uuid(),
      name: z.string(),
    }),
  ),
  suggestions: z.array(
    z.object({
      field: z.enum(
        Object.keys(exclusionFields) as [
          keyof typeof exclusionFields,
          ...Array<keyof typeof exclusionFields>,
        ],
      ),
      value: z.string(),
      provider: z.string().nullable(),
    }),
  ),
});
